import { useEffect, useState } from 'react';

import { logger } from '@/utils/logger/logger';

const TAG = 'useLocalStorage:';

/**
 * Custom hook for managing localStorage with React state.
 * Provides automatic synchronization between state and localStorage.
 *
 * @param key - The localStorage key to use
 * @param initialValue - The initial value if nothing is stored
 * @returns A tuple of [value, setValue] similar to useState
 *
 * @example
 * ```tsx
 * const [theme, setTheme] = useLocalStorage('theme', 'light');
 * const [width, setWidth] = useLocalStorage('sidebar_width', 220);
 * const [isOpen, setIsOpen] = useLocalStorage('sidebar_open', true);
 * ```
 */
export function useLocalStorage<T>(key: string, initialValue: T): [T, (value: T) => void] {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? (JSON.parse(item) as T) : initialValue;
    } catch (error) {
      // If error also return initialValue
      logger.warn(TAG, `Error reading localStorage key "${key}"`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that
  // persists the new value to localStorage.
  const setValue = (value: T) => {
    try {
      // Save state
      setStoredValue(value);
      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      logger.warn(TAG, `Error setting localStorage key "${key}"`, error);
    }
  };

  return [storedValue, setValue];
}

/**
 * Specialized hook for number values with min/max validation.
 * Useful for widths, heights, or any numeric settings with bounds.
 *
 * @param key - The localStorage key to use
 * @param initialValue - The initial value if nothing is stored
 * @param min - Minimum allowed value
 * @param max - Maximum allowed value
 * @returns A tuple of [value, setValue] with automatic clamping
 *
 * @example
 * ```tsx
 * const [width, setWidth] = useLocalStorageNumber('sidebar_width', 220, 180, 320);
 * // width will always be between 180 and 320
 * ```
 */
export function useLocalStorageNumber(
  key: string,
  initialValue: number,
  min: number,
  max: number
): [number, (value: number) => void] {
  const [storedValue, setStoredValue] = useLocalStorage(key, initialValue);

  // Validate and clamp the stored value
  const validatedValue = Math.min(Math.max(storedValue, min), max);

  // If stored value was out of bounds, update it
  useEffect(() => {
    if (storedValue !== validatedValue) {
      setStoredValue(validatedValue);
    }
  }, [storedValue, validatedValue, setStoredValue]);

  const setValue = (value: number) => {
    const clampedValue = Math.min(Math.max(value, min), max);
    setStoredValue(clampedValue);
  };

  return [validatedValue, setValue];
}
